/**
 * The text comms panel (M7 plan S31; spec §2.7): the speaker's name and code-drawn placeholder portrait, the node's
 * lines and its numbered replies; a "Show [log]" reply carries a Show tag and the log it presents. Since card M3
 * (dashboard spec §2.2) it is the body of the dashboard's Log › Comms section, whose shell gives the frame, title and
 * Close. With no conversation open it offers the waiting hail ("<name> is hailing · [O] Answer") or says no one is
 * hailing.
 *
 * Keys come through the dashboard's focus inputs (`FocusButton`s in group `comms`): up/down move, Enter picks; the
 * number keys are the dashboard's tab keys there. Re-rendered on change only (a hail, a pick).
 */
import type { ReactNode } from 'react'
import { FocusButton } from '../../ui/focus'
import { Portrait } from '../../ui/fleet/Portrait'
import type { CommsLabels, PanelModel } from './model'
import '../../ui/strategic/strategic.css'
import '../../ui/fleet/captains.css'
import './comms.css'

export interface CommsPanelProps {
  model: PanelModel | null
  labels: CommsLabels
  onReply(id: string): void
  /** With no conversation open: the waiting hail's Answer button (its text is the HUD prompt's). */
  answer?: { readonly text: string; onAnswer(): void }
}

export function CommsPanel(p: CommsPanelProps): ReactNode {
  const l = p.labels
  const m = p.model
  if (m === null) {
    return (
      <div className="sui cm-panel" aria-label={l.title} data-testid="comms-panel">
        {p.answer === undefined ? (
          <p className="sui-empty" data-testid="comms-empty">
            {l.noHail}
          </p>
        ) : (
          <div className="sui-actions">
            <FocusButton group="comms" index={0} className="sui-btn sui-primary" testId="comms-answer" onActivate={p.answer.onAnswer}>
              {p.answer.text}
            </FocusButton>
          </div>
        )}
      </div>
    )
  }
  return (
    <div className="sui cm-panel" aria-label={`${l.title}: ${m.name}`} data-testid="comms-panel" data-node={m.key}>
      <header className="sui-head cm-speaker">
        <Portrait recipe={m.portrait} size={64} className="cm-portrait" />
        <h2 className="sui-title" data-testid="comms-speaker" data-faction={m.faction ?? 'none'}>
          {m.name}
        </h2>
      </header>
      <section className="sui-section cm-lines" aria-live="polite">
        {m.lines.map((line, i) => (
          <p key={i} className="cm-line" data-testid="comms-line">
            {line}
          </p>
        ))}
      </section>
      <ol className="cm-replies">
        {m.replies.map((r, i) => (
          <li key={r.id}>
            <FocusButton
              group="comms"
              index={i}
              className={`sui-btn cm-reply${r.show ? ' cm-reply--show' : ''}`}
              testId={r.show ? 'comms-reply-show' : 'comms-reply'}
              onActivate={() => p.onReply(r.id)}
            >
              <span className="cm-reply__n">{r.n}.</span>
              {r.show ? <span className="cm-reply__tag">{l.show}</span> : null}
              <span className="cm-reply__text">{r.text}</span>
              {r.shows === null ? null : <span className="cm-reply__shows">{r.shows}</span>}
            </FocusButton>
          </li>
        ))}
      </ol>
    </div>
  )
}
